<template>
    <kgr-lightbox
        :value="show"
        :headline="$t('Preise ändern')"
        scrollable
        @post-input="$emit('close')"
    >
        <div style="max-width: 600px;">
            <kgr-loading v-if="loading" />
            <div v-if="requestConfirmation">
                <div
                    v-if="showError"
                    class="kgr-alert kgr-alert--info"
                >
                    {{ errorMessage }}
                </div>
                <div
                    v-else
                    class="kgr-alert kgr-alert--info"
                >
                    <kgr-icon
                        icon-class="info-circle"
                        style="margin-bottom: -5px;margin-right:5px;color:black;"
                        disabled
                        small
                    />
                    {{ $t('Durch Änderungen an Preisen können auch bereits abgerechnete Posten betroffen sein. Hierfür muss der Rechnungslauf erneut gestartet werden.') }} <br /><br />
                    <div class="kgr-text kgr-text--bold">
                        {{ $t('Willst Du die Änderungen wirklich speichern?') }}
                    </div>
                </div>
            </div>

            <div v-else-if="!loading">
                <dl class="kgr-definitionList kgr-definitionList--horizontal kgr-definitionList--125">
                    <dt
                        class="kgr-definitionList__term"
                    >
                        {{ $t('Neu') }}
                    </dt>
                    <dd
                        class="kgr-definitionList__definition"
                    >
                        <kgr-icon
                            icon-class="add"
                            circle
                            accent
                            @click="addPriceChange"
                        />
                    </dd>
                    <template
                        v-for="(priceChange, index) in priceHistory.priceChanges"
                        :key="'row' + index"
                    >
                        <dt class="kgr-definitionList__term">
                            {{ formatDate(priceChange.start) }}
                            <kgr-icon
                                v-if="priceChange.deleted"
                                icon-class="undo"
                                :title="$t('Löschen rückgängig machen')"
                                @click="undoDelete(priceChange)"
                            />
                            <kgr-icon
                                v-else
                                icon-class="delete"
                                :title="$t('Preisänderung löschen')"
                                @click="markDeleted(priceChange)"
                            />
                        </dt>
                        <dd class="kgr-definitionList__definition">
                            <kgr-accordion>
                                <kgr-accordion-item
                                    :open="editPricePerItem(index)"
                                >
                                    <template #header-left>
                                        <h5 class="kgr-headline kgr-headline--h5 kgr-headline--text kgr-headline--firstInText">
                                            {{ formatPricePerTime(priceChange.pricePerTime) }}
                                        </h5>
                                    </template>
                                    <label
                                        class="kgr-inputGroup"
                                    >
                                        <div
                                            class="kgr-inputGroup__label"
                                        >
                                            {{ $t('Beginn') }}
                                        </div>
                                        <kgr-date-picker
                                            v-model="priceChange.start"
                                        />
                                    </label>
                                    <kgr-price-per-time-input
                                        v-model="priceChange.pricePerTime"
                                        fixed-type
                                    />
                                    <kgr-accordion-item />
                                </kgr-accordion-item>
                            </kgr-accordion>
                        </dd>
                    </template>
                    <dt
                        class="kgr-definitionList__term"
                    >
                        {{ $t('Basispreis') }}
                    </dt>
                    <dd
                        class="kgr-definitionList__definition"
                    >
                        <kgr-accordion>
                            <kgr-accordion-item>
                                <template #header-left>
                                    <h5 class="kgr-headline kgr-headline--h5 kgr-headline--text kgr-headline--firstInText">
                                        {{ formatPricePerTime(priceHistory.basePrice) }}
                                    </h5>
                                </template>
                                <kgr-price-per-time-input
                                    v-model="priceHistory.basePrice"
                                    fixed-type
                                />
                            </kgr-accordion-item>
                        </kgr-accordion>
                    </dd>
                </dl>
                <div class="kgr-row">
                </div>
                <div
                    class="kgr-alert kgr-alert--info"
                >
                    <kgr-icon
                        icon-class="info-circle"
                        style="margin-bottom: -5px;margin-right:5px;color:black;"
                        disabled
                        small
                    />
                    {{ $t('Einträge, die keine echte Preisänderung darstellen, werden nicht gespeichert.') }}
                </div>
            </div>
        </div>
        <template #footer>
            <button
                class="kgr-button"
                @click="close"
            >
                {{ requestConfirmation ? $t('Zurück') : $t('cancel') }}
            </button>
            <button
                v-if="!showError || !requestConfirmation"
                class="kgr-button kgr-button--accent"
                @click="confirm"
            >
                {{ $t('save') }}
            </button>
        </template>
    </kgr-lightbox>
</template>

<script>
import { format, parse } from '@legacy/js/date/date-fns.js';
import Accordion from '../../common/accordion/Accordion.vue';
import AccordionItem from '../../common/accordion/AccordionItem.vue';
import AccountingClubFee from '../../../js/dto/AccountingClubFee.js'
import DatePicker from '../../common/form/picker/DatePicker.vue'
import Helper from '../../../js/accounting/Helper.js'
import Icon from '../../common/icon/Icon.vue'
import Lightbox from '../../common/modal/Lightbox.vue'
import Loading from '../../common/Loading.vue'
import PriceChange from '../../../js/dto/PriceChange.js'
import PriceHistory from '../../../js/dto/PriceHistory.js'
import PricePerTimeInput from './PricePerTimeInput.vue'
import axios from 'axios';

export default {
    components: {
        'kgr-accordion': Accordion,
        'kgr-accordion-item': AccordionItem,
        'kgr-lightbox': Lightbox,
        'kgr-loading': Loading,
        'kgr-date-picker': DatePicker,
        'kgr-price-per-time-input': PricePerTimeInput,
        'kgr-icon': Icon,
    },
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        fee: {
            type: AccountingClubFee,
            required: true,
        },
    },
    data() {
        return {
            helper: new Helper(),
            priceHistory: null,
            loading: false,
            requestConfirmation: false,
            validStartDates: true,
            editItems: [],
            showError: false,
            errorMessage: null
        }
    },
    computed: {
        validPriceChanges() {
            return this.priceHistory.priceChanges.find(priceChange => false === priceChange.pricePerTime.valid) == null;
        },
        valid() {
            return this.validPriceChanges && this.validStartDates && this.priceHistory.basePrice.valid !== false;
        },
    },
    watch: {
        fee() {
            return this.init();
        },
    },
    created() {
        this.init();
    },
    methods: {
        showErrorDialog(hintMessage) {
            this.showError = true;
            this.errorMessage = hintMessage;
        },
        hasDuplicates(array) {
            return (new Set(array)).size !== array.length;
        },
        checkDateDuplicates() {
            let startDates = this.priceHistory.priceChanges.filter(priceChange => !priceChange.deleted).map(priceChange => priceChange.start);
            this.validStartDates = !this.hasDuplicates(startDates);
            if (!this.validStartDates) {
                this.showErrorDialog(this.$t('Du verwendest identische Startdaten. Eine Preisänderung ist nur an Tagen möglich, an denen noch keine stattgefunden hat.'))
            }
            this.$forceUpdate(); //otherwise changed date is not updated instantly
        },
        formatPricePerTime(price) {
            return this.helper.getPricePerTime(price);
        },
        formatDate(date) {
            return date;
        },
        update() {
            this.checkDateDuplicates();
            if (!this.valid) {
                return;
            }
            this.loading = true;
            this.requestConfirmation = false;
            let priceChanges = this.priceHistory.priceChanges.filter(priceChange => !priceChange.deleted)
            let payload = new PriceHistory(this.priceHistory.basePrice, priceChanges).toData();
            axios
                .put('/club/club-fees/' + this.fee.id + '/price-history', payload)
                .then(() => {
                    this.$emit('updated');
                    this.editItems = [];
                })
                .catch(error => {
                    this.showErrorDialog(error)
                })
                .finally(() => this.loading = false)
            ;
        },
        undoDelete(priceChange) {
            priceChange.deleted = false;
            this.checkDateDuplicates()
        },
        markDeleted(priceChange) {
            priceChange.deleted = true;
            this.checkDateDuplicates()
        },
        togglePricePerItem(index, deleted) {
            if(deleted) {
                return;
            }
            this.$set(this.editItems, index, !this.editItems[index]);
        },
        editPricePerItem(index) {
            return this.editItems[index];
        },
        addPriceChange() {
            this.editItems = [];
            this.togglePricePerItem(0);
            let today = format(new Date(), 'dd.MM.yyyy');
            this.priceHistory.priceChanges.unshift(new PriceChange(today, this.fee.basePrice));
        },
        close() {
            if(!this.requestConfirmation) {
                this.init();
                this.$emit('close');
            } else {
                this.requestConfirmation = false;
                this.errorMessage = null;
                this.showError = false;
            }
        },
        confirm() {
            if(this.requestConfirmation) {
                this.update();
            } else {
                this.requestConfirmation = true;
            }
        },
        init() {
            this.editItems = [];
            this.showError = false;
            this.errorMessage = null;
            this.priceHistory = new PriceHistory(this.fee.basePrice, this.fee.priceHistory.priceChanges).clone();
            this.priceHistory.priceChanges.forEach(priceChange => {
                if (priceChange.start) {
                    const parsed = parse(priceChange.start, 'yyyy-MM-dd', new Date());
                    priceChange.start = format(parsed, 'dd.MM.yyyy');
                }
            });
        },
    },
}
</script>
